import { showError } from '@calendly/alert';
import { I18n } from '@calendly/i18n';
import { Logger } from '@calendly/logging-service';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { useDisableScrollBody } from '@calendly/ui/hooks/use-disable-scroll-body';
import { useFocusLock } from '@calendly/ui/hooks/use-focus-lock';
import classNames from 'classnames';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAiChatDiscoveryMiEligibleState, useAiChatRightRailVariantAllowed } from 'features/agent_hub';
import { useSettingsModalRightRailElevation } from 'features/app_shell/hooks/use_settings_modal_right_rail_elevation';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { useMeasureFloatingChromeObstruction } from 'features/layout';
import { useLeftNavigationContext, useLeftNavigationState } from 'features/left_navigation';
import { modalVisibleSelector } from 'features/one_off_modal';
import { BareOutlet, URL_STATE_PREFIX } from 'features/scoped_url_state';
import { getEffectiveRailWidth, isTruthyRailExpanded } from './get_effective_rail_width';
import { useRightRail } from './hooks/use_right_rail';
import { routes as defaultRoutes } from './routes';
const wrapperIsOpenCss = "wxdpn54";
const wrapperIsPhoneCss = "ww5tvry";
const wrapperCss = "wr4aa1r";
const Wrapper = forwardRef((props, ref) => {
  const {
    isOpen,
    isPhone,
    as: AsComponent,
    railWidth,
    pushWidth,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const styleProp = {
    style: {
      '--rail-width': railWidth,
      '--push-width': pushWidth
    }
  };
  return <Component ref={ref} data-testid="right-rail-wrapper" {...restProps} {...styleProp} className={classNames(wrapperCss, {
    [wrapperIsOpenCss]: isOpen,
    [wrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const containerIsOpenCss = "c1thswmg";
const containerIsPhoneCss = "cyq9w3n";
const containerRailExpandedCss = "c1peykwu";
const sidePanelPaneAsideCss = "s69w0vy";
const containerShellPanelCss = "c1cljqc0";
const containerSettingsModalElevatedCss = "c1c31mlr";

/** Legacy shell only; product-refresh shell lays the rail in `#shell-side-panel`.
 * TODO: Remove Wrapper, Container, BaseOutlet legacy path, and associated CSS when the new app shell is fully rolled out. */
const containerLegacyViewportCss = "c1o58t52";
const containerCss = "c1mlomng";
const Container = forwardRef((props, ref) => {
  const {
    isOpen,
    isPhone,
    railExpanded,
    isSettingsModalRightRailElevated,
    as: AsComponent,
    useLegacyViewportPosition,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} data-testid="right-rail-container" {...restProps} className={classNames(containerCss, {
    [containerIsOpenCss]: isOpen,
    [containerIsPhoneCss]: isPhone,
    [containerRailExpandedCss]: railExpanded,
    [containerSettingsModalElevatedCss]: isSettingsModalRightRailElevated,
    [containerLegacyViewportCss]: useLegacyViewportPosition,
    [containerShellPanelCss]: !useLegacyViewportPosition
  }, props.className)} />;
});
const AiChatRailVariantRenderer = ({
  pane,
  component: Component,
  ...props
}) => {
  const allowed = useAiChatRightRailVariantAllowed();
  const {
    close
  } = useRightRail();
  const dispatch = useDispatch();
  const propsForLogRef = useRef(props);
  propsForLogRef.current = props;
  useEffect(() => {
    if (!allowed) {
      Logger.error(`Unauthorized access to right rail pane: ${pane}`, {
        props: propsForLogRef.current
      });
      dispatch(showError(I18n.t('publisher.components.right_rail.unauthorized_error')));
      close();
    }
  }, [allowed, close, dispatch, pane]);
  if (!allowed) {
    return null;
  }
  return <Component {...props} />;
};

/**
 * `ai_chat`: MI eligibility first (no `useVariant` until eligible); then experiment variant gate.
 */
const AiChatRailDiscoveryRenderer = ({
  pane,
  component: Component,
  ...props
}) => {
  const {
    eligible,
    isMiSettingsPending
  } = useAiChatDiscoveryMiEligibleState();
  const {
    close
  } = useRightRail();
  const dispatch = useDispatch();
  const propsForLogRef = useRef(props);
  propsForLogRef.current = props;
  useEffect(() => {
    if (isMiSettingsPending) return;
    if (!eligible) {
      Logger.error(`Unauthorized access to right rail pane: ${pane}`, {
        props: propsForLogRef.current
      });
      dispatch(showError(I18n.t('publisher.components.right_rail.unauthorized_error')));
      close();
    }
  }, [close, dispatch, eligible, isMiSettingsPending, pane]);
  if (!eligible) {
    return null;
  }
  return <AiChatRailVariantRenderer pane={pane} component={Component} {...props} />;
};

/** No hooks here — delegates so `StandardRailRenderer` vs ai_chat tree each call hooks unconditionally. */
const RailRenderer = rendererProps => {
  if (rendererProps.pane === 'ai_chat') {
    return <AiChatRailDiscoveryRenderer {...rendererProps} />;
  }
  return <StandardRailRenderer {...rendererProps} />;
};
const StandardRailRenderer = ({
  pane,
  component: Component,
  useGuard,
  ...props
}) => {
  const {
    close
  } = useRightRail();
  const dispatch = useDispatch();
  const propsForLogRef = useRef(props);
  propsForLogRef.current = props;
  const isAllowed = useGuard?.(props) ?? true;
  useEffect(() => {
    if (!isAllowed) {
      Logger.error(`Unauthorized access to right rail pane: ${pane}`, {
        props: propsForLogRef.current
      });
      dispatch(showError(I18n.t('publisher.components.right_rail.unauthorized_error')));
      close();
    }
  }, [close, dispatch, isAllowed, pane]);
  if (!isAllowed) {
    return null;
  }
  return <Component {...props} />;
};
const BaseOutlet = ({
  isOpen = false,
  railWidth,
  railExpanded = false,
  children
}) => {
  const ref = useRef();
  const isOneOffModalVisible = useSelector(modalVisibleSelector);
  const {
    isCustomSideNav
  } = useLeftNavigationContext();
  const isProductRefreshAppShell = useProductRefreshAppShell();
  const settingsModalRightRailElevation = useSettingsModalRightRailElevation();
  const isSettingsModalRightRailElevated = isOpen && settingsModalRightRailElevation;
  const isPhone = useDeviceType('isPhone');
  const isTabletDown = useDeviceType('isTabletDown');
  const isLargeDesktopDown = useDeviceType('isLargeDesktopDown');
  useMeasureFloatingChromeObstruction('legacy-right-rail', ref, isOpen && !isPhone && !isProductRefreshAppShell && !isOneOffModalVisible);
  const pushWidth = useMemo(() => {
    if (isTabletDown || isLargeDesktopDown && isCustomSideNav || railExpanded) {
      return '0';
    }
    return railWidth;
  }, [isTabletDown, isLargeDesktopDown, isCustomSideNav, railExpanded, railWidth]);
  useFocusLock({
    isOpen: isOpen && isPhone && !isProductRefreshAppShell,
    wrapperRef: ref
  });
  useDisableScrollBody({
    isOpen: isOpen && isPhone && !isProductRefreshAppShell,
    wrapperRef: ref
  });

  // Product-refresh shell handles layout (width, position, mobile overlay) via SidePanel/MobileSidePanelPortal.
  if (isProductRefreshAppShell) {
    return children;
  }
  return <Wrapper isOpen={isOpen} isPhone={isPhone} pushWidth={pushWidth} railWidth={isPhone ? '100vw' : railWidth}>
      <Container ref={ref} isOpen={isOpen} isPhone={isPhone} railExpanded={railExpanded} isSettingsModalRightRailElevated={isSettingsModalRightRailElevated} useLegacyViewportPosition>
        {children}
      </Container>
    </Wrapper>;
};
const RailOutlet = ({
  routeName,
  contentKey,
  component: Component,
  route,
  ...props
}) => {
  const isPhone = useDeviceType('isPhone');
  const leftNavigationState = useLeftNavigationState();
  const railWidth = useMemo(() => getEffectiveRailWidth({
    routeWidth: route?.width,
    paneProps: {
      railExpanded: props.railExpanded
    },
    isPhone,
    leftNavigationState
  }), [route?.width, props.railExpanded, isPhone, leftNavigationState]);
  const rendererProps = {
    ...props
  };
  delete rendererProps.railExpanded;
  const railExpandedFlag = isTruthyRailExpanded(props.railExpanded);
  if (!route) {
    return <BaseOutlet isOpen={false} railWidth={route?.width ?? 0} />;
  }
  return <BaseOutlet isOpen railExpanded={railExpandedFlag} railWidth={railWidth}>
      <aside className={sidePanelPaneAsideCss}>
        <RailRenderer key={contentKey} pane={routeName} component={Component} useGuard={route.useGuard} {...rendererProps} />
      </aside>
    </BaseOutlet>;
};
export const Outlet = ({
  routes = defaultRoutes
}) => <BareOutlet renderer={RailOutlet} routes={routes} prefix={URL_STATE_PREFIX.RIGHT_RAIL} />;

require("./outlet.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./outlet.js");